import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { Loader2, MailCheck, ShieldAlert } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/language-switcher';
import { supportedLanguages } from '@/lang';
import { httpClient } from '@/lib/httpClient';
import { normalizeLanguageCode } from '@/lib/i18n/normalize-language';
import { useProvider } from '@/hooks/useTenant';
import { useTranslation } from 'react-i18next';

type VerificationPhase = 'idle' | 'loading' | 'success' | 'error';

interface VerifyResponse {
  status?: string;
  already_verified?: boolean;
  message?: string;
  redirect?: {
    url?: string | null;
    token?: string | null;
    source?: string | null;
  } | null;
  [key: string]: unknown;
}

const buildVerificationPath = (id: string, hash: string, query: string) => {
  const trimmedQuery = query.trim();
  const searchSuffix = trimmedQuery ? `?${trimmedQuery}` : '';
  return `/api/v1/customer/email/verify/${encodeURIComponent(id)}/${encodeURIComponent(hash)}${searchSuffix}`;
};

const resolveSupportedLanguage = (value: string | null | undefined): string | undefined => {
  const normalized = normalizeLanguageCode(value)?.toLowerCase();
  if (!normalized) {
    return undefined;
  }

  const directMatch = supportedLanguages.find((language) => language.code === normalized);
  if (directMatch) {
    return directMatch.code;
  }

  const baseCode = normalized.split(/[-_]/)[0];
  const baseMatch = supportedLanguages.find((language) => language.code === baseCode);
  return baseMatch?.code;
};

export default function VerifyEmailPage() {
  const { id, hash } = useParams<{ id: string; hash: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const provider = useProvider();
  const { t, i18n } = useTranslation();

  const [languageReady, setLanguageReady] = useState(false);
  const [phase, setPhase] = useState<VerificationPhase>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [responseDetails, setResponseDetails] = useState<VerifyResponse | null>(null);

  const queryString = useMemo(() => searchParams.toString(), [searchParams]);
  const languageParam = useMemo(() => searchParams.get('lang'), [searchParams]);
  const preferredLanguage = useMemo(() => {
    const browserLanguage = i18n.language ?? i18n.resolvedLanguage;
    return resolveSupportedLanguage(languageParam) ?? resolveSupportedLanguage(browserLanguage) ?? 'en';
  }, [i18n.language, i18n.resolvedLanguage, languageParam]);

  useEffect(() => {
    let isActive = true;

    const ensureLanguage = async () => {
      if (preferredLanguage && i18n.language !== preferredLanguage) {
        try {
          await i18n.changeLanguage(preferredLanguage);
        } finally {
          if (isActive) {
            setLanguageReady(true);
          }
        }
        return;
      }

      if (isActive) {
        setLanguageReady(true);
      }
    };

    void ensureLanguage();

    return () => {
      isActive = false;
    };
  }, [i18n, preferredLanguage]);

  useEffect(() => {
    if (!id || !hash) {
      setPhase('error');
      setErrorMessage('The verification link is missing required parameters.');
      return;
    }

    const controller = new AbortController();

    const verifyEmail = async () => {
      try {
        setPhase('loading');
        setErrorMessage('');

        const endpoint = buildVerificationPath(id, hash, queryString);
        const { data } = await httpClient.get<VerifyResponse>(endpoint, {
          signal: controller.signal,
        });

        setResponseDetails(data ?? null);
        setPhase('success');
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        const axiosError = error as AxiosError<VerifyResponse>;
        const message =
          axiosError.response?.data?.message ||
          (typeof axiosError.message === 'string' ? axiosError.message : '') ||
          'We could not verify your email address. Please request a new link.';

        setErrorMessage(message);
        setResponseDetails(axiosError.response?.data ?? null);
        setPhase('error');
      }
    };

    void verifyEmail();

    return () => {
      controller.abort();
    };
  }, [hash, id, queryString]);

  if (!languageReady) {
    return null;
  }

  const alreadyVerified = Boolean(responseDetails?.already_verified);
  const successHeadline = alreadyVerified
    ? t('auth.emailVerification.alreadyVerifiedTitle')
    : t('auth.emailVerification.successTitle');
  const successBody =
    responseDetails?.message ||
    (alreadyVerified
      ? t('auth.emailVerification.alreadyVerifiedBody')
      : t('auth.emailVerification.successBody'));
  const redirectUrl = responseDetails?.redirect?.url?.trim() || '';

  const handleSuccessNavigate = (fallback: string) => {
    if (redirectUrl) {
      window.location.assign(redirectUrl);
      return;
    }
    navigate(fallback);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12 text-foreground">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-xl space-y-8">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="text-xl font-semibold text-foreground">
            {provider?.name ?? t('home.customerCardTitle')}
          </div>
          <p className="text-sm text-muted-foreground">
            {t('auth.emailVerification.heading')}
          </p>
        </div>

        <Card>
          <CardHeader className="space-y-2">
            <CardTitle>{t('auth.emailVerification.cardTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {(phase === 'idle' || phase === 'loading') && (
              <div className="flex flex-col items-center gap-4 py-8 text-center text-muted-foreground">
                <Loader2 className="h-12 w-12 animate-spin" aria-hidden="true" />
                <p>{t('auth.emailVerification.verifying')}</p>
              </div>
            )}

            {phase === 'success' && (
              <div className="flex flex-col items-center gap-4 text-center">
                <MailCheck className="h-12 w-12 text-primary" aria-hidden="true" />
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">{successHeadline}</h2>
                  <p className="text-muted-foreground">{successBody}</p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button onClick={() => handleSuccessNavigate('/customer/login')}>
                    {t('auth.emailVerification.goToLogin')}
                  </Button>
                  <Button variant="secondary" onClick={() => handleSuccessNavigate('/')}>
                    {t('auth.emailVerification.home')}
                  </Button>
                </div>
              </div>
            )}

            {phase === 'error' && (
              <div className="flex flex-col items-center gap-4 text-center">
                <ShieldAlert className="h-12 w-12 text-destructive" aria-hidden="true" />
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">
                    {t('auth.emailVerification.errorTitle')}
                  </h2>
                  <p className="text-muted-foreground">{errorMessage}</p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button onClick={() => navigate('/customer/login')}>
                    {t('auth.emailVerification.returnToLogin')}
                  </Button>
                  <Button variant="secondary" onClick={() => navigate('/')}>
                    {t('auth.emailVerification.backToHome')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
